import React, { useState, useEffect } from 'react'
import DataTable from 'react-data-table-component'
import { getStudentIdData } from '../../lib/studentpersonalquestion'
import { getDataWithType } from '../../lib/parameters'
import { PersonalQuestionEdit } from './PersonalQuestionEdit'
import { getPersonalQuestionColumns } from '../../Data/PersonalQuestions/PersonalQuestionListData'

const PersonalQuestionList = ({ studentId }) => {
	const [questionList, setQuestionList] = useState([])
	const [studentQuestionList, setStudentQuestionList] = useState([])
	const [loading, setLoading] = useState(true)
	const [refreshTrigger, setRefreshTrigger] = useState(0)
	const [isDarkMode, setIsDarkMode] = useState(false)

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class']
		})
		return () => observer.disconnect()
	}, [])

	// Initial load and when studentId changes
	useEffect(() => {
		if (studentId) {
			getData()
		}
	}, [studentId])

	// Refresh when answer is updated
	useEffect(() => {
		if (refreshTrigger > 0) {
			refreshData()
		}
	}, [refreshTrigger])

	function getStudentData(qid) {
		if (studentQuestionList.length > 0) {
			const result = studentQuestionList.filter((item) => item.questionId === qid)
			return result.length > 0 ? result[0].id : 0
		} else return 0
	}

	function getStudentAnswer(qid) {
		if (studentQuestionList.length > 0) {
			const result = studentQuestionList.filter((item) => item.questionId === qid)

			let returnResult = ''

			if (result.length > 0) returnResult = result[0].studentAnswer?.length > 0 ? result[0].studentAnswer : 'No Answer'
			else returnResult = 'Never Answered'

			return returnResult
		} else return ''
	}

	function getAnswerStatus(qid) {
		if (studentQuestionList.length > 0) {
			const result = studentQuestionList.filter((item) => item.questionId === qid)
			if (result.length > 0 && result[0].studentAnswer?.length > 0) {
				return 'Answered'
			}
		}
		return 'Not Answered'
	}

	async function getData() {
		try {
			setLoading(true)
			const questions = await getDataWithType('PERSQUESTION')
			const sortedQuestionList = questions.sort((a, b) => {
				if (a.order < b.order) return -1
				if (a.order > b.order) return 1
				return 0
			})
			setQuestionList(sortedQuestionList)

			const stdQuest = await getStudentIdData(studentId)
			setStudentQuestionList(stdQuest)
		} catch (error) {
			console.error('Error fetching questions:', error)
		} finally {
			setLoading(false)
		}
	}

	async function refreshData() {
		try {
			// Silently refresh student answers without showing loading spinner
			const stdQuest = await getStudentIdData(studentId)
			setStudentQuestionList(stdQuest)
		} catch (error) {
			console.error('Error refreshing data:', error)
		}
	}

	const handleAnswerUpdate = () => {
		// Trigger refresh by incrementing the counter
		setRefreshTrigger((prev) => prev + 1)
	}

	// Expandable row component
	const ExpandedComponent = ({ data }) => (
		<div className="p-3">
			<PersonalQuestionEdit
				value={getStudentAnswer(data.id)}
				setValue={handleAnswerUpdate}
				id={getStudentData(data.id)}
				questionId={data.id}
				studentId={studentId}
			/>
		</div>
	)

	// Get columns with the status function
	const columns = getPersonalQuestionColumns(getAnswerStatus).map((col, index) => ({
		...col,
		width: index === 0 ? '5%' : index === 1 ? '80%' : '15%'
	}))

	const customStyles = {
		headCells: {
			style: {
				fontSize: '16px',
				fontWeight: '600',
				paddingLeft: '12px',
				paddingRight: '12px',
				color: isDarkMode ? '#fff' : '#000',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff',
				'&:nth-child(4)': {
					position: 'sticky',
					right: 0,
					zIndex: 3,
					backgroundColor: isDarkMode ? '#2c323f' : '#fff',
					boxShadow: '-2px 0 5px rgba(0,0,0,0.05)'
				}
			}
		},
		cells: {
			style: {
				fontSize: '15px',
				paddingLeft: '12px',
				paddingRight: '12px',
				color: isDarkMode ? '#fff' : '#000',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff',
				'&:nth-child(4)': {
					position: 'sticky',
					right: 0,
					zIndex: 2,
					backgroundColor: isDarkMode ? '#2c323f' : '#fff',
					boxShadow: '-2px 0 5px rgba(0,0,0,0.05)'
				}
			}
		},
		rows: {
			style: {
				minHeight: '50px',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff'
			}
		},
		pagination: {
			style: {
				color: isDarkMode ? '#fff' : '#000',
				backgroundColor: isDarkMode ? '#2c323f' : '#fff'
			}
		}
	}

	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<div className="card-body">
				<h5 className="mb-3">Personality Assessment Survey</h5>
				{loading ? (
					<div className="text-center">
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
						<p className="mt-2">Loading personality assessment...</p>
					</div>
				) : (
					<div style={{ overflowX: 'auto' }}>
						<DataTable
							columns={columns}
							data={questionList}
							pagination
							paginationPerPage={25}
							paginationRowsPerPageOptions={[25, 50, 75, 100]}
							highlightOnHover
							striped
							expandableRows
							expandableRowsComponent={ExpandedComponent}
							defaultSortFieldId={1}
							customStyles={customStyles}
							noDataComponent={
								<div className="text-center p-4">
									<p className="text-muted">No questions available</p>
								</div>
							}
						/>
					</div>
				)}
			</div>
		</div>
	)
}

export { PersonalQuestionList }
